import BaseModal from 'components/BaseModal'
import _, { isUndefined, sortBy } from 'lodash'
import { Tooltip, Collapse, Typography, Form } from 'antd';
import { MODAL_SIZE_EXTRA_LARGE } from 'constants/Layout'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { checkCharLimit, editorRules, FieldTypes, generateFormFields } from 'utils/formUtils';
import ProductAPI from 'restAPI/ProductAPI'
import SubProductAPI from 'restAPI/SubProductAPI'
import ProjectAPI from 'restAPI/ProjectAPI';
import FindingAPI from "restAPI/FindingAPI";
import SubProductEnvironmentTypeAPI from 'restAPI/SubProductEnvironmentTypeAPI'
import WizardFormV2 from 'components/WizardFormV2'
import Vocabulary, { getVocabulary } from 'components/Vocabulary';
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import axios from 'axios';
import message from 'components/CustomMessage';
import { disableFutureDates } from 'components/ColumnFilterDateRange';
import { envParseSelectOptions, EXPLOITED_OPTIONS, MANUAL_FINDING_MARK_DOWN_FIELDS, scanTypeOptions, TOOL_FINDING_CATEGORIES } from './Constants';
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import ManualFindingTemplateForm from './ManualFindingTemplateForm';
import ModalUtil from "utils/modalUtil";
import { DeleteIcon, EditIcon } from 'components/Icons';
import DeletePopup from "components/DeletePopup";
import { useActiveUserContext } from 'contexts/ActiveUserContext';
import ManualFindingsCVSSFields from 'views/Findings/ManualFindingsCVSSFields';
import htmlToMarkDown, { sanitizeMarkdown } from 'utils/htmlToMarkDownUtil';
import { marked } from 'marked';
import { getTemporarySessionStorage, MANUAL_FINDING_ENV_ID, MANUAL_FINDING_PRODUCT_ID, MANUAL_FINDING_SUB_PRODUCT_ID, setTemporarySessionStorage } from 'utils/sessionFilterStoreUtil';
import GlobalSettingAPI from 'restAPI/GlobalSettingAPI';
import { PermissionSetMap } from 'constants/RBACConstants';
import { hasPermission } from 'utils/authService';
import { removeNonPrintableUnicodeCharacters } from 'utils/fileUtils';
import Sorters from 'utils/sorters';
import { parseCWEOptions } from 'views/DraftFindingForm/DraftFindingFormMetaDataSection';
import { useHasDisabledEnvironment } from 'contexts/GlobalSettingsContext';
import ACCommentEditor from 'ac-components/ac-comment-editor/ACCommentEditor';
import { convertCommentToNewFormat } from './FindingDetails/common';

const modalProps = {
    footer: null,
    title: 'Add Manual Finding', 
    className: "modal-full-height no-footer", 
    width: MODAL_SIZE_EXTRA_LARGE
}

const DEFAULT_VALUES = {
    description: undefined,
    stepsToReproduce: undefined,
    mitigation: undefined,
    impact: undefined,
    severity: "CRITICAL",
    scanType: undefined,
    scanDate: undefined,
    testType: "Vulnerability",
    armorcodeCategory: undefined,
    cwe: undefined,
    cve: undefined,
    vendor: undefined,
    cvssCalculator: undefined,
    cvssVector: undefined,
    cvssScore: undefined
};

export default forwardRef(function ManualFindingPopup({name, productId, subProductId, environmentId, projectId, projectsDisabled = false, testType, ...props}, ref) {
    const [products, setProducts] = useState();
    const [subProducts, setSubProducts] = useState([]);
    const [environments, setEnvironments] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [formData, setFormData] = useState({});
    const [form, setForm] = useState();
    const [succeeded, setSucceeded] = useState(false);
    const activeUser = useActiveUserContext();
    const thisRef = useRef();
    const [fileList, setFileList] = useState([]);
    // const [file,setFile] = useState(null);
    const editorRefs = useRef({});
    const {hasDisabledEnvironment, envValue} = useHasDisabledEnvironment();
    const fetchTemplates = useCallback(() => {
        const { promise, cancel } = FindingAPI.getManualFindingTemplates();

         const enhancedPromise  = promise.then(data => {
            setTemplates(data);
            return data;
        }, _.noop)
        return { promise: enhancedPromise, cancel }
    },[]);
    
    useEffect(() => {
        const { cancel } = fetchTemplates();
        return cancel;
    },[fetchTemplates])

    useEffect(() => {
        const { promise, cancel } = ProductAPI.getAllQuick();
        promise.then(setProducts, _.noop);
        return cancel;
    }, []);

    useEffect(() => {
        if (!form) {
            return;
        }

        const { manualFindingProductId, manualFindingSubProductId, manualFindingEnvId } = getTemporarySessionStorage();
        const data = {};
        const product = productId || manualFindingProductId;
        const subProduct = subProductId || manualFindingSubProductId;
        const environment = environmentId || manualFindingEnvId;
        if (product) {
            data.product = product;
        }
        if (subProduct) {
            data.subProduct = subProduct;
        }
        if (projectId) {
            data.armorcodeProjects = projectId;
        }
        if (environment) {
            data.environment = environment;
        }
        if (projectId && testType) {
           data.testType = testType;
        }

        form.setFieldsValue(data);
        
        setFormData((formData) => ({ ...formData, ...data}));
    }, [form, productId, subProductId, projectId, environmentId, testType]);

    useEffect(() => {
        if(formData.product){
            setSubProducts(null);
            const {promise, cancel} = SubProductAPI.getAllQuick(formData.product);
            promise.then(setSubProducts, _.noop);
            return cancel;
        }
    }, [formData.product]);

    useEffect(() => {
        if(formData.subProduct && form){
            setEnvironments(null);
            const {promise, cancel} = SubProductEnvironmentTypeAPI.getAllQuick(formData.subProduct);
            promise.then(response => {
                const { manualFindingEnvId } = getTemporarySessionStorage();
                setEnvironments(response)
                const isEnvID = response.some(res => res.id === manualFindingEnvId);
                const productionId = response.find(env => env.name === "Production")?.id
                const environmentData = { environment: environmentId || (isEnvID ? manualFindingEnvId : productionId) }
                form.setFieldsValue(environmentData)
                setFormData((formData) => ({ ...formData, ...environmentData}));
            })
            return cancel;
        }
    }, [formData.subProduct, form, environmentId])

    useImperativeHandle(ref, () => ({ ...thisRef.current }));

    const uploadManualFindingAttachment = useCallback(async (file)=>{
        if(file){
            const {id, signedUrl} = await FindingAPI.uploadManualAttachment({fileName: removeNonPrintableUnicodeCharacters(file.name)}).promise;
            const attachmentRefIds = id;
            await axios({ method: 'put', url: signedUrl, data: file });
            return attachmentRefIds;
       }
    },[]);

    const processForm = useCallback((data) => {
        const datas = form.getFieldsValue()
        let attachmentRefIds;
        const attachmentRefIdsPromise = fileList.map((file)=>{
            return uploadManualFindingAttachment(file.originFileObj);
        });
        const promise = Promise.all(attachmentRefIdsPromise)
        .then(async (attachmentRefIdsPromiseData)=>{
            attachmentRefIds = attachmentRefIdsPromiseData.join();
            
            // convert html to markdown
            MANUAL_FINDING_MARK_DOWN_FIELDS.forEach(field => {
                if (data[field]) {
                   data[field] = sanitizeMarkdown(htmlToMarkDown(data[field]));
                }
            });
        
            delete data.cvssCalculator;
            const dropdown = datas.analysisType
            let environment = environments.find(env => env.id === data.environment)?.name;
            if(hasDisabledEnvironment){
                environment = envValue;
            }
            (["cwe", "cve"]).forEach(key => {
                if(!data[key]){
                    delete data[key];
                }
            });
            if(data.cwe){
                data.cwe = +data.cwe;
            }
            if(data.scanDate){
                data.scanDate = data.scanDate.utc().format('YYYY-MM-DD HH:mm:ss');
            }
        
            const cvssScoreString = data.cvssScore?.toString();
            if (_.isEmpty(data.cvssVector) && _.isEmpty(cvssScoreString)) {
                delete data.cvssVector;
                delete data.cvssScore;
            } else if (_.isEmpty(data.cvssVector)) {
                delete data.cvssVector;
            }
    
            data["staticFinding"] =  (dropdown === "STATIC") ? true : false;
            data["dynamicFinding"] =  dropdown === "DYNAMIC" ? true : false
            data["attachmentRefIds"] = attachmentRefIds;
            const key = new Date().toTimeString();
            const blob = new Blob([JSON.stringify([data])], { type: 'text/plain' });
            const fileObject = new File([blob], 'custom' + key + '.json', { type: "application/json" });
            const _data = await ToolsIntegrationsAPI.postUploadUrl({
                environment,
                fileName: 'custom' + key + '.json',
                product: data.product,
                subProduct: data.subProduct,
                armorcodeProjects: data.armorcodeProjects,
                tools: "CUSTOM",
                cvssVector: !_.isEmpty(data.cvssVector) ? data.cvssVector : undefined,
                cvssScore: !_.isEmpty(cvssScoreString) ? data.cvssScore : undefined
            }, 'CUSTOM').promise;
            await axios({ method: 'put', url: _data.signedUrl, data: fileObject });

            message.success("Successfully added new Finding");

            setSucceeded(true);

            thisRef.current.close();

            // store data in temporary storage
            setTemporarySessionStorage(MANUAL_FINDING_PRODUCT_ID, data.product);
            setTemporarySessionStorage(MANUAL_FINDING_SUB_PRODUCT_ID, data.subProduct);
            setTemporarySessionStorage(MANUAL_FINDING_ENV_ID, data.environment);
        })
        .catch((err)=>{
            console.log(err);
        });
        return promise;
    },
    [environments, form,fileList, thisRef, uploadManualFindingAttachment, hasDisabledEnvironment, envValue]);

    const onChange = useCallback((data) => {
        if (form) {
            if (_.has(data, 'product')) {
                form.resetFields(["subProduct", "environment"]);
            } else if (_.has(data, 'subProduct')) {
                form.resetFields(["environment"]);
            }
        }
        setFormData((formData) => ({ ...formData, ...data }))
    }, [form]);

    const fileChange = useCallback(
        (file,maxSize) => {
          if (file && file.file) {
            const {fileList} = file;
            for (let i = 0; i < fileList.length; i++) {
                if((fileList[i].size) /1000000 > maxSize){
                    setFileList([]);
                    return;
                }
            }
            setFileList(fileList.slice());
            // if(!((file.file.size) /1000000 > maxSize)){
            //     let fileList = file.fileList.slice();
            //     setFileList(fileList);
            //     // setFile(file.file)
            // }else{
            //     setFileList([]);
            // }
          }
        },
        [],
    );

    const getDetailedFields = useMemo(() => {
        return [
            getMaunalFindingTemplateField(templates, setTemplates, form, activeUser, editorRefs, 24),
            ...getDetailedInfoFields(form, editorRefs, [], formData, environments)
        ]
    }, [form, editorRefs, templates, activeUser, formData, environments])

    const pages = [{
        name: "Basic Info",
        fields: getBasicInfoFields({ products, subProducts, environments, projectsDisabled, showPenTesting: props.showPenTesting, hasDisabledEnvironment })
    }, {
        name: "Detailed Info",
        fields: getDetailedFields
    }, {
        name: "Attach file",
        fields: getAttachmentFields({fileChange,fileList})
    }]

    return (
        <BaseModal
            modalProps={modalProps}
            ref={thisRef}
            afterClose={() => props.afterClose(succeeded)}
        >
            <WizardFormV2
                validateOnBackwardStep={false}
                onCancel={thisRef.current?.close}
                pages={pages}
                onChange={onChange}
                formRef={setForm}
                initialValues={{ severity: "CRITICAL", testType: "Vulnerability" }}
                api={processForm}
            />
        </BaseModal>
    )
})

export function getAttachmentFields({fileChange,maxSize=50,fileList}){
    return [{
            name: "file",
            label: "Attach file",
            type: FieldTypes.ATTACHMENT,
            uploadProps: {
                multiple: true,
                beforeUpload:() => false,
                onChange:(e) => fileChange(e,maxSize),
                fileList
            },
            rules: [{
                    validator: (_rule, files={}) => {
                        // const { file } = files;
                        const {fileList=[]} = files;
                        for (let i = 0; i < fileList.length; i++) {
                            if((fileList[i].size) /1000000 > maxSize){
                                return Promise.reject("All file size should be less than 50 MB");
                            }
                        }
                        // if (file) {
                        //     if((file.size) /1000000 > maxSize){
                        //         return Promise.reject("All file size should be less than 50 MB");
                        //     }
                        // }
                        return Promise.resolve();
                }
            }]
    }];
}

export function getMessageEditorFields(editorRefs) {
    const renderEditor = (field) =>  (
        <ACCommentEditor placeholder={field.placeholder} showFooter={false} defaultFocus={true} users={[]} ref={(_ref) => {
            editorRefs.current[field.name] = _ref;
        }} />
    );

    return [{
        name: "description",
        label: <span className='required-mark'>Description</span>,
        placeholder: 'Description',
        render: renderEditor,
        NoDefaultLengthRule: true,
        help:"Describe the vulnerability in detail so that it could be understood easily.",
        rules: [editorRules({ maxByteSizeInKB: 100, maxMessage: true })],
        valuePropName: "initialValue",
        colSpan: 24,
    },
    {
        name: "stepsToReproduce",
        label: <Vocabulary name="stepToReproduce" />,
        placeholder: getVocabulary("stepToReproduce"),
        render: renderEditor,
        NoDefaultLengthRule: true,
        rules: [editorRules({ required: false, maxByteSizeInKB: 100, maxMessage: true })],
        valuePropName: "initialValue",
        colSpan: 24,
    },
    {
        name: "mitigation",
        label: <span className='required-mark'><Vocabulary name="mitigation" /></span>,
        placeholder: getVocabulary("mitigation"),
        render: renderEditor,
        NoDefaultLengthRule: true,
        help:"Mentions the Mitigation steps to fix or essentially reduces the likelihood of a vulnerability being exploited.",
        rules: [editorRules({ maxByteSizeInKB: 100, maxMessage: true })],
        valuePropName: "initialValue",
        colSpan: 24,
    },
    {
        name: "impact",
        label: <span className='required-mark'><Vocabulary name="impact" /></span>,
        placeholder: 'Impact',
        render: renderEditor,
        NoDefaultLengthRule: true,
        toolTipPlacement:"topLeft",
        help:"Mention the impacts of this vulnerability in this section.",
        rules: [editorRules({ maxByteSizeInKB: 32, maxMessage: true })],
        valuePropName: "initialValue",
        colSpan: 24,
    }]
}

const AdditionalFields = ({form, ingnoreFields = [], formData, environments}) => {
    const hasCollapseMounted = useRef(false);
    const [activeKey, setActiveKey] = useState(['additionalFields']);
    const cwes = form.getFieldValue('cwe')
    
    const selectedEnvironmentName = useMemo(() => {
        return environments?.find(env => env.id === formData?.environment)?.name;
    }, [environments, formData?.environment]);

    const getCWEOptions = useCallback(
        (params) => {
          const cwe = !isUndefined(cwes) ? [cwes] : []
          const { promise, cancel } = FindingAPI.getCWEs(params);
          return {
            cancel,
            promise: promise.then((response) => {
              return [...response.content, ...(cwe || [])].sort();
            }),
          };
        },
        [cwes]
      );
    // Open and close Collapse component on mount, as we won't receive populated vlaues unless fields are rendered at least once
    useEffect(() => {
     if (!hasCollapseMounted.current) {
        hasCollapseMounted.current = true;
        setActiveKey(undefined);
     }   
    }, []);

    const fields = [
        {
            name: "severity",
            label: <Vocabulary name="severity" />,
            type: FieldTypes.SELECT,
            options: ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFO"].map(s => ({label: getVocabulary(s), value: _.upperCase(s)})),
            colSpan: 12
        },
        {
            name: "scanType",
            label: "Scan Type",
            type: FieldTypes.SELECT,
            options: scanTypeOptions,
            colSpan: 12,
            props: {
              allowClear: true,
            },
        },
        {
            name: "scanDate",
            label: "Scan Date",
            props: {
                style: { width: '100%' },
                disabledDate: disableFutureDates,
            },
            type: FieldTypes.DATE,
            colSpan: 12
        }, 
        {
            name: "testType",
            label: <>Tool Finding <Vocabulary name="category" /></>,
            type: FieldTypes.SELECT,
            options: TOOL_FINDING_CATEGORIES,
            colSpan: 12
        },
        {
            name: "cwe",
            label: "CWE",
            colSpan: 12,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            notFoundContent: "No CWE Vulnerability is present",
            props: {
                api: getCWEOptions,
                parseSelectOptions: parseCWEOptions,
                optionsMutator: (options) => sortBy(options, "value"),
                // apiParams: cweParams
            }
        }, 
        {
            name: "cve",
            label: "CVE",
            colSpan: 12,
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            notFoundContent: "No CVE Vulnerability is present",
            props: {
                api: FindingAPI.getCVEs,
                // apiParams: cveParams
            }
        },
        {
            name: 'cvssCalculator',
            render: () => <ManualFindingsCVSSFields form={form} subProductId={formData?.subProduct} environmentName={selectedEnvironmentName}/>
        },
        {
            name: "vendor",
            label: "Vendor",
            NoDefaultLengthRule: true,
            rules: [
                {max: 100,message: "Vendor should be maximum 100 characters"}],
                colSpan: 12
        },
        {
            name: "",
            label: "",
            colSpan: 12,
            render: () => null
        },
        {
            label: "Component Name",
            name: "componentName",
            NoDefaultLengthRule: true,
            rules: [() => checkCharLimit(100)],
            colSpan: 12
        },
        {
            label: "Component Affected Version",
            name: "componentVersion",
            NoDefaultLengthRule: true,
            rules: [() => checkCharLimit(100)],
            colSpan: 12
        },
        {
            label: "Component Fix Version",
            name: "componentFixVersions",
            NoDefaultLengthRule: true,
            rules: [() => checkCharLimit(100)],
            colSpan: 12
        },
        {
            label: "Exploit Available",
            name: "exploited",
            type: FieldTypes.SELECT,
            options: EXPLOITED_OPTIONS,
            colSpan: 12
        }
    ].filter(f => !ingnoreFields.includes(f.name))
    return (
        <Collapse activeKey={activeKey} onChange={setActiveKey} bordered={false} className={`m-b-sm list-collapse`}>
            <Collapse.Panel header={<Typography.Text strong>Additional Fields</Typography.Text>} key={'additionalFields'} className="no-border">
            <Form layout="vertical"
                form={form}
                className="team-form"
                >
                  {generateFormFields(fields)}
              </Form>
            </Collapse.Panel>
        </Collapse>
      )
}

export function getDetailedInfoFields(form, editorRefs, ingnoreFields = [], formData, environments){

    const fields = [
        {
            name: "title",
            label: "Summary",
            NoDefaultLengthRule: true,
            rules: [{required: true, message: "Please enter the summary of the finding"},
            {min: 3,message: "Summary should be atleast 3 characters"},
            {max: 255,message: "Summary should be maximum 255 characters"}
        ],
        },
        {
            name: "armorcodeCategory",
            label: <>Findings <Vocabulary name="category"/></>,
            type: FieldTypes.SELECT,
            props: {
                api: GlobalSettingAPI.getCustomizedCategory,
            },
        },
        ...getMessageEditorFields(editorRefs),
        {
            name: "additionalFields",
            render: () => <AdditionalFields form={form} ingnoreFields={ingnoreFields} formData={formData} environments={environments}/>
        }
    ];
    return fields;
}


function getBasicInfoFields({ products, subProducts, environments, projectsDisabled, showPenTesting, hasDisabledEnvironment}){
    const commonFields = [
    {
        name: "product",
        label:  <Vocabulary name="product" />,
        rules: [{required: true, message: <>Please select a  <Vocabulary name="product" isLowerCase={true} /></>}],
        type: FieldTypes.SELECT,
        options: products,
        loading: _.isEmpty(products),
        help: <>ArmorCode  <Vocabulary name="product" /> name in which we need to ingest the findings.</>,
        colSpan: 12
    }, {
        name: "subProduct",
        label:  <Vocabulary name="subProduct" />,
        rules: [{required: true, message: <>Please select a <Vocabulary name="subProduct" isLowerCase={true} /></>}],
        type: FieldTypes.SELECT,
        options: subProducts,
        loading: _.isNull(subProducts),
        help: <>ArmorCode  <Vocabulary name="subProduct" /> name of the selected  <Vocabulary name="product" /> in which we need to ingest the findings.</>,
        colSpan: 12
    }, ...(hasDisabledEnvironment ? [] : [{
        name: "environment",
        label: "Environment",
        rules: [{required: true, message: "Please select an environment"}],
        type: FieldTypes.SELECT,
        options: envParseSelectOptions(environments),
        loading: _.isNull(environments),
        help: <>ArmorCode Environment of the selected  <Vocabulary name="subProduct" />  in which we need to ingest the findings. Such as <EnvironmentName name={"Production"} />, <EnvironmentName name={"Staging"} />, etc.</>,
        colSpan: 12
    }]),
    {
        name: "armorcodeProjects",
        label:  showPenTesting ? "Pen Testing" :<Vocabulary name="project" />,
        type: FieldTypes.MULTI_SELECT,
        props: {
            api: ProjectAPI.v2.getAll,
            disabled: !hasPermission(PermissionSetMap.UPDATE_FINDINGS_PROJECT) || !hasPermission(PermissionSetMap.UPDATE_PROJECT) || projectsDisabled
        },
        help: <>ArmorCode  <Vocabulary name="project" /> name in which we need to ingest the findings.</>,
        colSpan: 12
    },
    // {
    //     name: "shouldCreateJicket",
    //     label: <ComingSoonTooltip>I want to create a Jira ticket</ComingSoonTooltip>,
    //     type: FieldTypes.BOOLEAN,
    //     inline: true,
    //     disabled: true,
    // }
];
    return commonFields;
}

const getMaunalFindingTemplateField = (templates, setTemplates, form, activeUser, editorRefs, colSpan=12) => {
    const setFindingTemplateData = (templates, templateName, fieldName = 'template') => {
        const currentTemplate = templates[templateName];

        if(currentTemplate){
            const template = { ...currentTemplate.template };
            // convert markdown to html
            MANUAL_FINDING_MARK_DOWN_FIELDS.forEach(field => {
                if (template[field]) {
                    template[field] = convertCommentToNewFormat(marked.parse(template[field]));
                    if (editorRefs.current && editorRefs.current[field].setValue) {
                        editorRefs.current[field].setValue(template[field])
                    }
                }
            })
            form.setFieldsValue({ [fieldName]: templateName });
            form.setFieldsValue({ ...DEFAULT_VALUES, ...template });
        }
    }

    const onPopupClose = (templateData) => {
        if (!templateData) {
            return;
        }
        const { name, oldName, template, createdBy } = templateData
        const newTemplates = {..._.omit(templates, [oldName]), [name]: { name, createdBy, template } };
        setFindingTemplateData(newTemplates, name);
        setTemplates(newTemplates);
    };

    const onTemplateAddAction = (e) => {
        e.stopPropagation();
        ModalUtil.show({
            content: <ManualFindingTemplateForm templates={templates}/>,
            afterClose: onPopupClose,
        })
    };

    const editTemplate = (e, templateName) => {
        e.stopPropagation();
        ModalUtil.show({
            content: <ManualFindingTemplateForm templates={templates} templateData={templates[templateName]}/>,
            afterClose: onPopupClose,
        })
    };

    const handleTemplate = (templateName) => {
        if (templateName) {
            setFindingTemplateData(templates, templateName);
        }
    };

    const onDeleteTemplate = (e, templateName) => {
        e.stopPropagation();
        ModalUtil.show({
            content: (
                <DeletePopup 
                    promptMessage={`You want to delete template ${templateName}?`}
                    note={false}
                    confirmText="Delete"
                    onDelete={async () => {
                        await FindingAPI.deleteManualFindingTemplates(templateName);
                        setTemplates(_.omit(templates, [templateName]));
                        form.setFieldsValue({ template: null });
                        message.success("Template Deleted Successfully");
                    }}
                />
            )
        })
    };

    const renderLabel = (templateData, templateName) => {
        const disableTemplateActions = templateData.createdBy !== activeUser.email
        return (
        <div className='d-flex align-items-center justify-content-between'>
        <span>{templateName}</span>
        {!disableTemplateActions && <div className='d-inline-block'>
            <Tooltip title='Edit template' placeholder="top">
                <EditIcon className="m-r-xs" onClick={(e) => editTemplate(e, templateName)}/>
            </Tooltip>
            <Tooltip title='Delete template' placeholder="top">
                <DeleteIcon className="danger" onClick={(e) => onDeleteTemplate(e, templateName)}/>
            </Tooltip>
        </div>}
        </div>
    )}

    const options =  _.map(templates, (templateData, templateName) => ({ label: renderLabel(templateData, templateName), value: templateName })).sort(Sorters.STRING("value"));

    return {
        name: "template",
        label:  'Template',
        type: FieldTypes.SELECT_WITH_ACTION,
        btnText:"Add Template",
        btnType:"dashed",
        onActionClick: onTemplateAddAction,
        props: {
            onChange: handleTemplate,
            autoFocus: true,
            optionFilterProp: "value"
        },
        options:options,
        colSpan
    }
}
